import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
  useAuth,
} from '@aaas/shared';
import poolService from '../services/pool.service';
import type { Pool, PoolCreateInput, PoolUpdateInput } from '../types';

interface PoolFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  pool?: Pool | null;
  onSuccess: () => void;
}

export function PoolFormDialog({ open, onClose, mode, pool, onSuccess }: PoolFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or pool changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && pool) {
        setName(pool.name);
        setDescription(pool.description || '');
      } else {
        setName('');
        setDescription('');
      }
      setError(null);
    }
  }, [open, mode, pool]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: PoolCreateInput = {
          name: name.trim(),
          description: description.trim() || undefined,
        };
        await poolService.createPool(currentWorkspace.id, token, data);
      } else if (pool) {
        const data: PoolUpdateInput = {
          name: name.trim(),
          description: description.trim() || undefined,
        };
        await poolService.updatePool(pool.id, currentWorkspace.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} pool:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('pool.createTitle') : t('pool.editTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('pool.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('pool.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('pool.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('pool.descriptionPlaceholder')}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('pool.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
